import { useCallback, useEffect, useMemo, useState } from 'react';
import tprmService from '../../../services/compliance/tprm.service';
import { useComplianceAccess } from '../../../hooks/useComplianceAccess';
import {
  PerformanceRecommendation,
  VendorPerformanceReview,
  VendorPerformanceReviewCreate,
} from '../../../types/compliance';
import PanelNotifications, {
  usePanelNotifications,
} from '../../../components/compliance/PanelNotifications';


const RECOMMENDATION_BADGE: Record<PerformanceRecommendation, string> = {
  continue: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  watch: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  escalate: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  terminate: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
};


interface Props {
  vendorId: string;
}


function overallAccent(score: number): string {
  if (score >= 85) return 'text-green-700 dark:text-green-300';
  if (score >= 70) return 'text-blue-700 dark:text-blue-300';
  if (score >= 50) return 'text-amber-700 dark:text-amber-300';
  return 'text-red-700 dark:text-red-300';
}


export default function PerformanceReviewsPanel({ vendorId }: Props) {
  const { isManager: canReview } = useComplianceAccess();
  // Destructured to keep stable refs on notifyX — the aggregate object
  // changes on every error/success state write and would otherwise
  // invalidate the `load` useCallback and re-fire its effect, producing an
  // infinite fetch loop on endpoint failures.
  const {
    error: toastError,
    success: toastSuccess,
    clear: clearToasts,
    notifyError,
    notifyFailure,
  } = usePanelNotifications();

  const [reviews, setReviews] = useState<VendorPerformanceReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [period, setPeriod] = useState('');
  const [reviewDate, setReviewDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const [sla, setSla] = useState(80);
  const [comm, setComm] = useState(80);
  const [sec, setSec] = useState(80);
  const [delivery, setDelivery] = useState(80);
  const [rec, setRec] = useState<PerformanceRecommendation>('continue');
  const [notes, setNotes] = useState('');

  const previewOverall = useMemo(
    () => Math.round((sla + comm + sec + delivery) / 4),
    [sla, comm, sec, delivery],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await tprmService.listPerformanceReviews(vendorId);
      setReviews(rows);
    } catch (error) {
      notifyFailure('Failed to load performance reviews', error);
    } finally {
      setLoading(false);
    }
  }, [vendorId, notifyFailure]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setPeriod('');
    setReviewDate(new Date().toISOString().slice(0, 10));
    setSla(80);
    setComm(80);
    setSec(80);
    setDelivery(80);
    setRec('continue');
    setNotes('');
  };

  const submit = useCallback(async () => {
    if (!period.trim()) {
      notifyError({ title: 'Review period required', message: 'e.g. "Q1 2026"' });
      return;
    }
    const body: VendorPerformanceReviewCreate = {
      review_period: period.trim(),
      review_date: reviewDate,
      sla_adherence_score: sla,
      communication_score: comm,
      security_posture_score: sec,
      delivery_score: delivery,
      recommendation: rec,
      notes: notes.trim() || undefined,
    };
    setSaving(true);
    try {
      const created = await tprmService.createPerformanceReview(vendorId, body);
      setReviews((prev) => [created, ...prev]);
      setFormOpen(false);
      resetForm();
    } catch (error) {
      notifyFailure('Failed to save review', error);
    } finally {
      setSaving(false);
    }
  }, [vendorId, period, reviewDate, sla, comm, sec, delivery, rec, notes, notifyError, notifyFailure]);

  return (
    <div className="space-y-4">
      <PanelNotifications
        error={toastError}
        success={toastSuccess}
        onClearError={clearToasts}
        onClearSuccess={clearToasts}
      />

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-300">
          {loading
            ? 'Loading…'
            : `${reviews.length} review${reviews.length === 1 ? '' : 's'} recorded.`}
        </div>
        {canReview && !formOpen && (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            + Log Review
          </button>
        )}
      </div>

      {formOpen && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-panel space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block mb-1 text-label">Period</span>
              <input
                type="text"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                placeholder="Q1 2026"
                className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-label">Review date</span>
              <input
                type="date"
                value={reviewDate}
                onChange={(e) => setReviewDate(e.target.value)}
                className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: 'SLA adherence', value: sla, setter: setSla },
              { label: 'Communication', value: comm, setter: setComm },
              { label: 'Security posture', value: sec, setter: setSec },
              { label: 'Delivery', value: delivery, setter: setDelivery },
            ].map((s) => (
              <label key={s.label} className="text-sm">
                <span className="flex items-center justify-between mb-1 text-label">
                  <span>{s.label}</span>
                  <span className="tabular-nums font-medium">{s.value}</span>
                </span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={s.value}
                  onChange={(e) => s.setter(Number(e.target.value))}
                  className="w-full"
                />
              </label>
            ))}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-300">Overall</div>
            <div className={`text-2xl font-bold ${overallAccent(previewOverall)}`}>
              {previewOverall}
            </div>
          </div>

          <label className="text-sm block">
            <span className="block mb-1 text-label">Recommendation</span>
            <select
              value={rec}
              onChange={(e) => setRec(e.target.value as PerformanceRecommendation)}
              className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
            >
              <option value="continue">Continue</option>
              <option value="watch">Watch</option>
              <option value="escalate">Escalate</option>
              <option value="terminate">Terminate</option>
            </select>
          </label>

          <label className="text-sm block">
            <span className="block mb-1 text-label">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
            />
          </label>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => { setFormOpen(false); resetForm(); }}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Review'}
            </button>
          </div>
        </div>
      )}

      {!loading && reviews.length === 0 && (
        <div className="text-sm text-muted">
          No performance reviews yet.
        </div>
      )}

      <ul className="space-y-2">
        {reviews.map((r) => (
          <li
            key={r.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-panel"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm text-muted">
                  {r.review_period} · {r.review_date}
                </div>
                <div className="mt-0.5 flex items-center gap-2">
                  <span className={`text-3xl font-bold ${overallAccent(r.overall_score)}`}>
                    {r.overall_score}
                  </span>
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded uppercase ${RECOMMENDATION_BADGE[r.recommendation]}`}
                  >
                    {r.recommendation}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-gray-700 dark:text-gray-200">
                  <div>SLA: <span className="tabular-nums font-medium">{r.sla_adherence_score}</span></div>
                  <div>Comm: <span className="tabular-nums font-medium">{r.communication_score}</span></div>
                  <div>Security: <span className="tabular-nums font-medium">{r.security_posture_score}</span></div>
                  <div>Delivery: <span className="tabular-nums font-medium">{r.delivery_score}</span></div>
                </div>
                {r.notes && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                    {r.notes}
                  </p>
                )}
              </div>
              <div className="text-xs text-muted text-right whitespace-nowrap">
                Reviewed by<br />
                <span className="font-medium text-label">
                  {r.reviewed_by_email || r.reviewed_by_oid}
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
